import React, { useEffect, useState } from "react";
import { Layout, Menu } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styles from "./main-layout.module.css";
import Header from "../header/header";
import Footer from "../footer/footer";
import Routes from "../../routes/index";
import SVGIcon from "../../constants/SVGIcon";
import menuItems from "../../constants/menu-items";
import { getCookie } from "../../utils/cookie-storage/cookie-storage";
import RoutesConstant from "../../constants/routes";
import { toast } from "react-toastify";
import { useAbility } from "../../ability/AbilityContext";
import AccountModuleRedirectionModal from "../../components/account-module-redirection-modal/account-module-redirection-modal";
import useLogout from "../../hooks/useLogout";

const { SubMenu } = Menu;
const { Sider, Content } = Layout;

export function MainLayout() {
  const { ability } = useAbility();
  const logout = useLogout();
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedKey, setSelectedKey] = useState("1");
  const [parentKey, setParentKey] = useState(null);
  const [openKeys, setOpenKeys] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const noLayoutRoutes = [RoutesConstant.RESET_PASSWORD_FIRST_LOGIN];
  const isNoLayoutPage = noLayoutRoutes.includes(location.pathname);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1030);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleOpenChange = (keys) => {
    if (!isMobile) {
      const latestKey = keys.find((key) => !openKeys.includes(key));
      if (latestKey) {
        setOpenKeys([latestKey]);
      } else {
        setOpenKeys([]);
      }
    } else {
      setOpenKeys(keys);
    }
  };

  useEffect(() => {
    const defaultRoute = menuItems[0];
    const allRoutes = menuItems.flatMap((item) =>
      item.children ? [item, ...item.children] : [item]
    );

    const selectedRoute = allRoutes.find((item) => {
      const matchPaths = [item?.path, ...(item?.aliasPaths || [])];
      return matchPaths.some((path) => path === location?.pathname);
    });

    const selected = selectedRoute?.key || defaultRoute?.key;
    setSelectedKey(selected);

    const parent = menuItems.find((item) =>
      item.children?.some((child) => child.key === selected)
    );

    const parentKeyValue = parent?.key ?? null;
    setParentKey(parentKeyValue);

    if (parentKeyValue) {
      setOpenKeys([parentKeyValue]);
    } else {
      setOpenKeys([]);
    }
  }, [location.pathname]);

  const isAuthPage = getCookie("auth-token");

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const showModal = () => setIsModalOpen(true);

  const handleOk = async () => {
    window.open(
      process.env.REACT_APP_ACCOUNTING_REDIRECT,
      "_blank",
      "noopener, noreferrer"
    );
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const checkAccess = (key) => {
    return ability.can("read", key) || ["logout"].includes(key);
  };

  const handleClick = async (item) => {
    if (item?.key === "logout") {
      await logout();
      toast.success("Logout successfully!");
    } else if (item.key === "accounting") {
      window.open(
        process.env.REACT_APP_ACCOUNTING_REDIRECT,
        "_blank",
        "noopener, noreferrer"
      );
      // showModal();
    } else {
      navigate(item.path);
    }
  };

  const hasChildren = (item) => {
    return item?.children && item.children.length > 0;
  };

  useEffect(() => {
    if (isMobile) {
      setOpenKeys([]);
    }
  }, [location.pathname]);

  return (
    <Layout className={styles.siteLayout}>
      {isAuthPage && !isNoLayoutPage ? (
        <>
          <Header collapsed={collapsed} toggleCollapsed={toggleCollapsed} />
          <Layout style={{ height: "90%" }}>
            <Sider
              trigger={null}
              breakpoint="md"
              collapsible
              collapsed={collapsed}
              onCollapse={(value) => setCollapsed(value)}
              onBreakpoint={(broken) => setCollapsed(broken)}
              collapsedWidth={70}
              width={300}
              className={`custom-sider ${
                collapsed ? "sider-collapsed" : "sider-open"
              }`}
              style={{ height: "100%" }}
            >
              <Menu
                theme="light"
                mode="inline"
                selectedKeys={[selectedKey, parentKey]}
                onOpenChange={handleOpenChange}
              >
                {menuItems?.map((item) => {
                  if (
                    checkAccess(item?.key) ||
                    item?.children?.some((subItem) => checkAccess(subItem?.key))
                  ) {
                    const { Icon } = item;
                    // Check if the item has sub-items (children)
                    if (hasChildren(item)) {
                      const isSelectedParent = item?.children
                        ?.map((subItem) => subItem?.key)
                        ?.includes(selectedKey);
                      return (
                        <SubMenu
                          key={item.key}
                          onOpenChange={(keys) => setOpenKeys(keys)}
                          icon={
                            typeof item?.Icon === "string" ? (
                              <SVGIcon
                                style={{ marginLeft: "8px" }}
                                name={item?.Icon}
                                width={"24"}
                                height={"24"}
                                className="anticon"
                                stroke={isSelectedParent ? "#FFF" : "#344054"}
                              />
                            ) : (
                              <Icon
                                style={{
                                  marginLeft: "8px",
                                  fill: `${isSelectedParent ? "#FFF" : "#344054"}`,
                                }}
                              />
                            )
                          }
                          title={item.label}
                        >
                          {item.children.map((subItem) => {
                            if (checkAccess(subItem?.key)) {
                              const { Icon: SubIcon } = subItem;
                              const isSelectedChild =
                                selectedKey === subItem.key;
                              return (
                                <Menu.Item
                                  key={subItem.key}
                                  icon={
                                    typeof subItem?.Icon === "string" ? (
                                      <SVGIcon
                                        style={{ marginLeft: "8px" }}
                                        name={subItem?.Icon}
                                        width={"24"}
                                        height={"24"}
                                        className="anticon"
                                        stroke={
                                          isSelectedChild
                                            ? "rgba(57, 181, 74, 1)"
                                            : "#344054"
                                        }
                                      />
                                      ) : (
                                        <SubIcon
                                          style={{
                                            marginLeft: "8px",
                                            fill: `${isSelectedChild ? "rgba(57, 181, 74, 1)" : "#344054"}`,
                                          }}
                                        />
                                      )
                                  }
                                  onClick={() => {
                                    setSelectedKey(subItem.key);
                                    setParentKey(item.key);
                                    navigate(subItem.path);

                                    if (isMobile) {
                                      setOpenKeys([]);
                                    } else {
                                      setOpenKeys([item.key]);
                                    }
                                  }}
                                >
                                  {subItem.label}
                                </Menu.Item>
                              );
                            } else return null;
                          })}
                        </SubMenu>
                      );
                    } else {
                      // Render regular Menu.Item for top-level items without sub-items
                      const isSelectedParent =
                        selectedKey === item.key || parentKey === item.key;
                      return (
                        <>
                          <Menu.Item
                            key={item?.key}
                            icon={
                              typeof item?.Icon === "string" ? (
                                <SVGIcon
                                  style={{ marginLeft: "8px" }}
                                  name={item?.Icon}
                                  width={"24"}
                                  height={"24"}
                                  className="anticon"
                                  stroke={isSelectedParent ? "#FFF" : "#344054"}
                                />
                                ) : (
                                  <Icon
                                    style={{
                                      marginLeft: "8px",
                                      fill: `${isSelectedParent ? "#FFF" : "#344054"}`,
                                    }}
                                  />
                                )
                            }
                            onClick={() => handleClick(item)}
                          >
                            {collapsed ? (
                              ""
                            ) : (
                              <Link to={item?.path}>{item?.label}</Link>
                            )}
                          </Menu.Item>
                          <div>
                            <AccountModuleRedirectionModal
                              handleCancel={handleCancel}
                              handleOk={handleOk}
                              showModal={showModal}
                              isModalOpen={isModalOpen}
                            />
                          </div>
                        </>
                      );
                    }
                  } else return null;
                })}
              </Menu>
            </Sider>
            <Layout>
              <Content className={styles.siteLayoutContent}>
                <Routes />
              </Content>
              <Footer />
            </Layout>
          </Layout>
        </>
      ) : (
        <Content>
          <Routes />
        </Content>
      )}
    </Layout>
  );
}

export default MainLayout;
